import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import styled from "styled-components";
import {
  fetchCheckoutConf,
  fetchAddPaymentSourceContext,
  finalizeAddPaymentSource,
  setProcessPaymentEndpointMode,
} from "../../api/api";
import PaymentMethodSelector from "./PaymentMethodSelector";
import SessionExpiredOverlay from "./SessionExpiredOverlay";
import HostedPageError from "../../pages/HostedPageError";
import { useCheckout } from "../../context/CheckoutContext";
import { useTranslation } from "../../hooks/useTranslation";
import { resolveHostedPageErrorType, isConfigurationIssue } from "../../utils/hostedPageError";
import { clearHostedPaymentReturnParams } from "../../utils/hostedPaymentReturn";
import { getShopperCitFailureMessage } from "../../utils/paymentOutcome";
import { minorToMajor } from "../../i18n/utils/helpers";
import { safeRedirect } from "../../utils/redirectUrlBuilder";
import { updatePageMetadata } from "../../utils/metadataUtils";

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** How often the page re-reads session state so it can flip to the terminal
 *  "card saved" screen no matter which path (sync, 3DS, webhook) completed it. */
const SESSION_POLL_INTERVAL_MS = 5000;

const Page = styled.div`
  min-height: 100vh;
  background: #f6f8fb;
  display: flex;
  justify-content: center;
  padding: 40px 16px 64px;
  font-family: -apple-system, BlinkMacSystemFont, "Inter", "Segoe UI",
    sans-serif;
`;

const Container = styled.div`
  width: 100%;
  max-width: 520px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  text-align: center;
`;

const Logo = styled.img`
  max-height: 44px;
  max-width: 200px;
  object-fit: contain;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
  letter-spacing: -0.4px;
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: #64748b;
  margin: 0;
  line-height: 1.6;
`;

const Card = styled.div`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  box-shadow: 0 4px 24px rgba(15, 23, 42, 0.05);
  padding: 24px;
`;

const ContextRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  padding: 6px 0;
  font-size: 14px;
`;

const ContextLabel = styled.span`
  color: #64748b;
`;

const ContextValue = styled.span`
  color: #0f172a;
  font-weight: 600;
  text-align: right;
`;

const DueBanner = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
  background: #fffbeb;
  border: 1.5px solid #fcd34d;
  border-radius: 12px;
  padding: 14px 16px;
`;

const DueBannerText = styled.p`
  margin: 0;
  font-size: 14px;
  color: #92400e;
  line-height: 1.6;
`;

const CountdownPill = styled.div`
  align-self: center;
  padding: 6px 14px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  background: ${(props) => (props.$urgent ? "#fde2e1" : "#eef2f7")};
  color: ${(props) => (props.$urgent ? "#b3261e" : "#334155")};
`;

const StatusCard = styled(Card)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  text-align: center;
  padding: 40px 32px;
`;

const STATUS_ICON_BACKGROUNDS = {
  success: "#dcfce7",
  warning: "#fef3c7",
  error: "#fee2e2",
};

const StatusIcon = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(props) =>
    STATUS_ICON_BACKGROUNDS[props.$variant] || "#f1f5f9"};
`;

const StatusHeading = styled.h2`
  font-size: 20px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
`;

const StatusText = styled.p`
  font-size: 14px;
  color: #64748b;
  margin: 0;
  line-height: 1.6;
`;

const RedirectLink = styled.a`
  margin-top: 8px;
  padding: 11px 24px;
  background: #0f172a;
  color: #ffffff;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  text-decoration: none;
  &:hover {
    color: #ffffff;
    opacity: 0.9;
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 15px 24px;
  border: none;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  color: ${(props) => (props.disabled ? "#666666" : "#ffffff")};
  background: ${(props) =>
    props.disabled ? "#cccccc" : props.$color || "#0f172a"};
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  transition: opacity 0.15s ease;
  &:hover:not(:disabled) {
    opacity: 0.9;
  }
`;

const SubmitNote = styled.p`
  margin: 10px 0 0;
  font-size: 12px;
  color: #64748b;
  text-align: center;
  line-height: 1.6;
`;

const Spinner = styled.div`
  width: 28px;
  height: 28px;
  border: 3px solid #e2e8f0;
  border-top-color: #2563eb;
  border-radius: 50%;
  animation: aps-spin 0.8s linear infinite;
  @keyframes aps-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

/** Verbatim gateway decline reason, kept visually distinct from our own copy. */
const DeclineReason = styled.p`
  margin: 0;
  padding: 10px 14px;
  border-radius: 10px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  font-size: 13px;
  color: #475569;
  line-height: 1.6;
`;

const SuccessCheck = () => (
  <svg
    width="28"
    height="28"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#16a34a"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const WarningMark = () => (
  <svg
    width="28"
    height="28"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#b45309"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

/** Invoice amounts are stored in minor units, so convert before formatting. */
const formatAmount = (amountMinor, currency, formatters) => {
  const numeric = Number(amountMinor);
  if (!Number.isFinite(numeric)) return null;
  const major = minorToMajor(numeric, currency);
  try {
    return formatters.currency(major, currency || "USD");
  } catch {
    return `${major.toFixed(2)} ${currency || ""}`.trim();
  }
};

/**
 * Add-payment-source hosted page (/checkout/a/:sessionId).
 *
 * Thin sibling of CheckoutPage for ADD_PAYMENT_SOURCE sessions: card-only,
 * zero-amount SETUP tokenization through the same gateway forms, with a
 * due-invoice summary and a distinct "card saved" terminal state. All card
 * submissions are routed to process-setup via setProcessPaymentEndpointMode.
 */
const AddPaymentSourcePage = () => {
  const { sessionId } = useParams();
  const [searchParams] = useSearchParams();
  const { t, formatters } = useTranslation();
  const {
    setIsHostedPageMode,
    setClientIp,
    setClientCountry,
    merchantName,
    setMerchantName,
    logoUrl,
    setLogoUrl,
    setBrandColor,
    setCitPaymentErrorMessage,
    triggerPaymentEvent,
  } = useCheckout();

  const [context, setContext] = useState(null);
  const [confDto, setConfDto] = useState(null);
  const [errorType, setErrorType] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [loading, setLoading] = useState(true);

  // phase: form | finalizing | success | pending
  const [phase, setPhase] = useState("form");
  const [finalizeResult, setFinalizeResult] = useState(null);
  // What happened to the outstanding balance. Saving the card and collecting the balance are
  // two outcomes: the card can be vaulted successfully and the charge still be declined.
  const [collection, setCollection] = useState(null);

  const [selectedMethod, setSelectedMethod] = useState("");
  const [secondsLeft, setSecondsLeft] = useState(null);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [awaitingOutcome, setAwaitingOutcome] = useState(false);

  const expiresAtRef = useRef(null);
  const finalizeAttemptedRef = useRef(false);
  const pendingTransactionIdRef = useRef(null);
  const redirectTimerRef = useRef(null);
  // Set when the shopper chooses to enter another card after an unusable one. The session
  // stays COMPLETED throughout, so this is what stops polling from bouncing them straight
  // back to the terminal screen before the replacement card has been submitted.
  const declinedTransactionIdRef = useRef(null);

  const dueCount = Number(context?.due_invoices?.count) || 0;
  const dueTotalLabel = formatAmount(
    context?.due_invoices?.total_amount,
    context?.due_invoices?.currency || context?.currency,
    formatters
  );

  const collectionStatus = collection?.status || null;
  const needsNewCard = collectionStatus === "CARD_UNUSABLE";
  const collectionAmountLabel = formatAmount(
    collection?.amount,
    collection?.currency || context?.currency,
    formatters
  );
  const collectionRemaining = Number(collection?.remaining) || 0;

  // The selected card form owns tokenization and submission; the CTA fires the
  // same shared trigger the classic checkout button uses.
  const handleSubmit = useCallback(async () => {
    if (submitting || !selectedMethod || sessionExpired) return;
    setCitPaymentErrorMessage(null);
    setSubmitting(true);
    setAwaitingOutcome(true);
    try {
      await triggerPaymentEvent?.();
    } finally {
      setSubmitting(false);
    }
  }, [
    submitting,
    selectedMethod,
    sessionExpired,
    setCitPaymentErrorMessage,
    triggerPaymentEvent,
  ]);

  // Route all card form submissions to the zero-amount SETUP endpoint while
  // this page is mounted.
  useEffect(() => {
    setProcessPaymentEndpointMode("setup");
    setIsHostedPageMode(true);
    return () => {
      setProcessPaymentEndpointMode("payment");
      setIsHostedPageMode(false);
      setCitPaymentErrorMessage(null);
      if (redirectTimerRef.current) clearTimeout(redirectTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Tenant branding (shared /conf cache with MetadataBootstrap).
  useEffect(() => {
    let cancelled = false;
    fetchCheckoutConf()
      .then((data) => {
        if (cancelled || !data) return;
        setConfDto(data);
        setLogoUrl(data.logoUrl || "");
        setBrandColor(data.colour || "");
        setMerchantName(data.companyName || data.brand || "");
        updatePageMetadata(data);
      })
      .catch(() => {
        // Branding is cosmetic — the page still works without it.
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Client geo — used by the card forms for IP / country fallbacks.
  useEffect(() => {
    let cancelled = false;
    const fetchGeo = async () => {
      try {
        const response = await fetch("https://api.country.is/");
        if (response.ok) {
          const data = await response.json();
          if (!cancelled && data?.country) {
            setClientIp(data.ip || "");
            setClientCountry(String(data.country).toUpperCase());
            return;
          }
        }
      } catch {
        // fall through to IP-only lookup
      }
      try {
        const fallback = await fetch("https://api.ipify.org?format=json");
        const data = await fallback.json();
        if (!cancelled) setClientIp(data?.ip || "");
      } catch {
        if (!cancelled) setClientIp("");
      }
    };
    fetchGeo();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyContext = useCallback((data) => {
    setContext(data);
    if (data?.transaction_id) {
      pendingTransactionIdRef.current = data.transaction_id;
    }
    if (data?.due_collection) {
      setCollection(data.due_collection);
    }
    if (data?.expires_at_epoch_ms) {
      expiresAtRef.current = data.expires_at_epoch_ms;
    } else if (data?.remaining_seconds) {
      expiresAtRef.current = Date.now() + data.remaining_seconds * 1000;
    }
    // The session that saved an unusable card stays COMPLETED while the shopper enters a
    // replacement, so only a different transaction may move the page to the terminal screen.
    const stillTheDeclinedAttempt =
      declinedTransactionIdRef.current &&
      String(data?.transaction_id || "") === declinedTransactionIdRef.current;
    if (data?.session_status === "COMPLETED" && !stillTheDeclinedAttempt) {
      setPhase((prev) => (prev === "success" ? prev : "success"));
    }
  }, []);

  // Initial context load.
  useEffect(() => {
    if (!sessionId || !UUID_PATTERN.test(sessionId)) {
      setErrorType("not_found");
      setLoading(false);
      return;
    }
    let cancelled = false;
    fetchAddPaymentSourceContext(sessionId)
      .then((data) => {
        if (cancelled) return;
        applyContext(data);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        const backendMessage =
          err?.response?.data?.error || err?.response?.data?.message;
        setErrorType(resolveHostedPageErrorType(err?.response?.status, backendMessage));
        setErrorMessage(isConfigurationIssue(backendMessage) ? backendMessage : null);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [sessionId, applyContext]);

  // 3DS / redirect return: ?trx_id=...&status=... → verify with finalize.
  useEffect(() => {
    const trxId = searchParams.get("trx_id");
    if (!trxId || !UUID_PATTERN.test(trxId) || finalizeAttemptedRef.current) {
      return;
    }
    if (!sessionId || !UUID_PATTERN.test(sessionId)) return;
    pendingTransactionIdRef.current = trxId;
    finalizeAttemptedRef.current = true;
    setPhase("finalizing");

    let attempts = 0;
    const maxAttempts = 5;

    const applyFinalizeResult = (result) => {
      if (result?.status === "success") {
        setFinalizeResult(result);
        if (result.due_collection) setCollection(result.due_collection);
        setPhase("success");
        clearHostedPaymentReturnParams();
        return true;
      }
      if (result?.status === "failed") {
        setCitPaymentErrorMessage(getShopperCitFailureMessage(t));
        clearHostedPaymentReturnParams();
        setPhase("form");
        return true;
      }
      return false;
    };

    const runFinalize = async () => {
      attempts += 1;
      try {
        const result = await finalizeAddPaymentSource(sessionId, trxId);
        if (applyFinalizeResult(result)) {
          return;
        }
        // pending — the gateway confirmation hasn't landed yet.
        if (attempts < maxAttempts) {
          setTimeout(runFinalize, 3000);
        } else {
          setPhase("pending");
          clearHostedPaymentReturnParams();
        }
      } catch {
        setCitPaymentErrorMessage(getShopperCitFailureMessage(t));
        clearHostedPaymentReturnParams();
        setPhase("form");
      }
    };
    runFinalize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, sessionId]);

  const resolvePendingTransactionId = useCallback(() => {
    const fromUrl = searchParams.get("trx_id");
    if (fromUrl && UUID_PATTERN.test(fromUrl)) {
      return fromUrl;
    }
    const fromContext = pendingTransactionIdRef.current || context?.transaction_id;
    if (fromContext && UUID_PATTERN.test(String(fromContext))) {
      return String(fromContext);
    }
    return null;
  }, [searchParams, context?.transaction_id]);

  const runFinalizeForPendingTransaction = useCallback(async () => {
    const trxId = resolvePendingTransactionId();
    if (!trxId || !sessionId || !UUID_PATTERN.test(sessionId)) {
      return false;
    }
    try {
      const result = await finalizeAddPaymentSource(sessionId, trxId);
      if (result?.status === "success") {
        setFinalizeResult(result);
        if (result.due_collection) setCollection(result.due_collection);
        setPhase("success");
        return true;
      }
      if (result?.status === "failed") {
        setCitPaymentErrorMessage(getShopperCitFailureMessage(t));
        setPhase("form");
        return true;
      }
    } catch {
      // Keep polling — a transient error should not strand the shopper.
    }
    return false;
  }, [resolvePendingTransactionId, sessionId, setCitPaymentErrorMessage, t]);

  // Poll session state only once an outcome is in flight. The gateway forms submit on
  // their own, so this is the page's only way to learn the setup completed — whether the
  // form handled the response itself or a webhook confirmed it later. Polling continues
  // after a shopper-visible failure on purpose: a late gateway confirmation can still
  // complete the session. Before the first submit nothing can complete, so we stay quiet.
  const hasContext = Boolean(context);
  useEffect(() => {
    const waitingForOutcome =
      phase === "pending" ||
      phase === "finalizing" ||
      (phase === "form" && awaitingOutcome);
    if (!waitingForOutcome || !hasContext || errorType || sessionExpired) {
      return undefined;
    }
    const interval = setInterval(async () => {
      try {
        if (phase === "pending" || phase === "finalizing") {
          const resolved = await runFinalizeForPendingTransaction();
          if (resolved) {
            return;
          }
        }
        const data = await fetchAddPaymentSourceContext(sessionId);
        applyContext(data);
        if (data?.session_status === "COMPLETED") {
          setPhase("success");
        }
      } catch {
        // Transient errors are fine; the next tick retries.
      }
    }, SESSION_POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [
    phase,
    awaitingOutcome,
    hasContext,
    errorType,
    sessionExpired,
    sessionId,
    applyContext,
    runFinalizeForPendingTransaction,
  ]);

  // Session TTL countdown.
  useEffect(() => {
    if (!context || errorType) return undefined;
    if (phase === "success") return undefined;
    const tick = () => {
      if (!expiresAtRef.current) return;
      const remaining = Math.floor((expiresAtRef.current - Date.now()) / 1000);
      setSecondsLeft(Math.max(0, remaining));
      if (remaining <= 0) setSessionExpired(true);
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [context, errorType, phase]);

  // Merchant redirect after success (only when the merchant configured one). Suppressed when
  // the balance still needs a different card — bouncing the shopper away would strand them.
  useEffect(() => {
    if (phase !== "success" || needsNewCard) return;
    const redirectUrl = finalizeResult?.hosted_redirect_page;
    if (!redirectUrl) return;
    redirectTimerRef.current = setTimeout(() => {
      safeRedirect(redirectUrl, { target: "self" });
    }, 3500);
    return () => clearTimeout(redirectTimerRef.current);
  }, [phase, finalizeResult, needsNewCard]);

  // Second attempt after an unusable card. The first card stays vaulted on purpose: it costs
  // nothing to keep, and dunning can still fall back to it.
  const handleUseAnotherCard = useCallback(() => {
    declinedTransactionIdRef.current = String(
      context?.transaction_id || pendingTransactionIdRef.current || ""
    );
    // Any 3DS return params belong to the declined attempt.
    finalizeAttemptedRef.current = true;
    clearHostedPaymentReturnParams();
    setCollection(null);
    setFinalizeResult(null);
    setSelectedMethod("");
    setAwaitingOutcome(false);
    setCitPaymentErrorMessage(null);
    setPhase("form");
  }, [context?.transaction_id, setCitPaymentErrorMessage]);

  if (loading) {
    return (
      <Page>
        <Container>
          <StatusCard>
            <Spinner />
            <StatusText>
              {t("addPaymentSource.loading", {
                defaultValue: "Loading secure page…",
              })}
            </StatusText>
          </StatusCard>
        </Container>
      </Page>
    );
  }

  if (errorType) {
    return (
      <HostedPageError
        errorType={errorType}
        message={errorMessage}
        merchantName={merchantName}
        logoUrl={logoUrl}
        returnUrl={confDto?.checkoutRedirectUrl}
      />
    );
  }

  const routingEntries = context?.routing_entries || [];
  const itemDetails = {
    currencyCode: context?.currency || "",
    price: 0,
    billingPlatformName: "",
  };
  const formData = {
    firstName: context?.customer?.first_name || "",
    paymentMethod: selectedMethod,
  };

  const planName = context?.subscription?.plan_name;
  const urgent = typeof secondsLeft === "number" && secondsLeft <= 60;
  const countdownLabel =
    typeof secondsLeft === "number"
      ? `${Math.floor(secondsLeft / 60)}:${String(secondsLeft % 60).padStart(2, "0")}`
      : null;

  /**
   * Terminal-screen copy per collection outcome. Only CARD_UNUSABLE asks for another card:
   * a decline the card may clear on its own (no funds today, a velocity limit) is left to
   * dunning, because sending that shopper hunting for a second card is both wrong and
   * pointless — the same card usually works on the retry.
   */
  const resolveTerminalCopy = () => {
    if (needsNewCard) {
      return {
        title: t("addPaymentSource.result.unusable.title", {
          defaultValue: "We couldn't charge that card",
        }),
        text: collectionAmountLabel
          ? t("addPaymentSource.result.unusable.text", {
              defaultValue:
                "Your card was saved, but {{amount}} could not be charged to it. Please try a different card to settle the outstanding balance.",
              variables: { amount: collectionAmountLabel },
            })
          : t("addPaymentSource.result.unusable.textNoAmount", {
              defaultValue:
                "Your card was saved, but the outstanding balance could not be charged to it. Please try a different card.",
            }),
      };
    }
    if (collectionStatus === "RETRY_SCHEDULED") {
      return {
        title: t("addPaymentSource.result.retry.title", {
          defaultValue: "Payment method saved",
        }),
        text: collectionAmountLabel
          ? t("addPaymentSource.result.retry.text", {
              defaultValue:
                "We couldn't collect {{amount}} just now. Your card is saved and we'll retry automatically — you don't need to do anything.",
              variables: { amount: collectionAmountLabel },
            })
          : t("addPaymentSource.result.retry.textNoAmount", {
              defaultValue:
                "We couldn't collect the outstanding balance just now. Your card is saved and we'll retry automatically.",
            }),
      };
    }
    if (collectionStatus === "COLLECTED") {
      return {
        title: t("addPaymentSource.result.paid.title", {
          defaultValue: "Payment method saved",
        }),
        text: collectionRemaining > 0
          ? t("addPaymentSource.result.paid.textWithRemaining", {
              defaultValue:
                "Your card is saved and {{amount}} has been paid. We're charging the remaining invoices now.",
              variables: { amount: collectionAmountLabel },
            })
          : t("addPaymentSource.result.paid.text", {
              defaultValue:
                "Your card is saved and {{amount}} has been paid. It is now the primary payment method for this subscription.",
              variables: { amount: collectionAmountLabel },
            }),
      };
    }
    return {
      title: t("addPaymentSource.success.title", {
        defaultValue: "Payment method saved",
      }),
      text: dueCount > 0
        ? t("addPaymentSource.success.collectingText", {
            defaultValue:
              "Your new card is now the primary payment method for this subscription. We're charging the outstanding invoices to it now and will let you know if anything needs your attention.",
          })
        : t("addPaymentSource.success.text", {
            defaultValue:
              "Your new card is now the primary payment method for this subscription and will be used for future payments.",
          }),
    };
  };

  const renderStatus = () => {
    if (phase === "finalizing") {
      return (
        <StatusCard>
          <Spinner />
          <StatusHeading>
            {t("addPaymentSource.finalizing.title", {
              defaultValue: "Verifying your card…",
            })}
          </StatusHeading>
          <StatusText>
            {t("addPaymentSource.finalizing.text", {
              defaultValue:
                "One moment while we confirm your payment method with your bank.",
            })}
          </StatusText>
        </StatusCard>
      );
    }
    if (phase === "pending") {
      return (
        <StatusCard>
          <Spinner />
          <StatusHeading>
            {t("addPaymentSource.pending.title", {
              defaultValue: "Verification in progress",
            })}
          </StatusHeading>
          <StatusText>
            {t("addPaymentSource.pending.text", {
              defaultValue:
                "Your payment method is still being verified. You can safely close this page, we'll finish setting it up automatically.",
            })}
          </StatusText>
        </StatusCard>
      );
    }
    // Terminal state. The card is saved in every branch below — what differs is whether the
    // outstanding balance cleared, which the shopper is entitled to know before leaving.
    const { title, text } = resolveTerminalCopy();
    const redirectUrl = needsNewCard
      ? null
      : finalizeResult?.hosted_redirect_page ||
        confDto?.checkoutRedirectUrl ||
        null;
    return (
      <StatusCard>
        <StatusIcon $variant={needsNewCard ? "warning" : "success"}>
          {needsNewCard ? <WarningMark /> : <SuccessCheck />}
        </StatusIcon>
        <StatusHeading>{title}</StatusHeading>
        <StatusText>{text}</StatusText>
        {needsNewCard && collection?.message && (
          <DeclineReason>{collection.message}</DeclineReason>
        )}
        {needsNewCard && (
          <SubmitButton
            type="button"
            $color={confDto?.colour}
            disabled={sessionExpired}
            onClick={handleUseAnotherCard}
          >
            {t("addPaymentSource.result.useAnotherCard", {
              defaultValue: "Try a different card",
            })}
          </SubmitButton>
        )}
        {redirectUrl && (
          <>
            <StatusText>
              {t("addPaymentSource.success.redirecting", {
                defaultValue: "Returning you to the merchant…",
              })}
            </StatusText>
            <RedirectLink href={redirectUrl}>
              {t("addPaymentSource.success.continue", {
                defaultValue: "Continue",
              })}
            </RedirectLink>
          </>
        )}
      </StatusCard>
    );
  };

  return (
    <Page>
      <Container>
        {sessionExpired && phase !== "success" && (
          <SessionExpiredOverlay
            canRestart={false}
            merchantName={merchantName}
            returnUrl={confDto?.checkoutRedirectUrl}
          />
        )}

        <Header>
          {logoUrl && <Logo src={logoUrl} alt={merchantName || "Merchant"} />}
          <Title>
            {t("addPaymentSource.title", {
              defaultValue: "Update payment method",
            })}
          </Title>
          {merchantName && (
            <Subtitle>
              {t("addPaymentSource.subtitle", {
                defaultValue: "Add a new card for your subscription with",
              })}{" "}
              {merchantName}
            </Subtitle>
          )}
        </Header>

        {phase === "form" ? (
          <>
            {countdownLabel && !sessionExpired && (
              <CountdownPill $urgent={urgent}>
                {t("addPaymentSource.sessionExpiresIn", {
                  defaultValue: "Session expires in",
                })}{" "}
                {countdownLabel}
              </CountdownPill>
            )}

            {(planName || context?.customer?.email_masked) && (
              <Card>
                {planName && (
                  <ContextRow>
                    <ContextLabel>
                      {t("addPaymentSource.plan", { defaultValue: "Plan" })}
                    </ContextLabel>
                    <ContextValue>{planName}</ContextValue>
                  </ContextRow>
                )}
                {context?.customer?.email_masked && (
                  <ContextRow>
                    <ContextLabel>
                      {t("addPaymentSource.account", {
                        defaultValue: "Account",
                      })}
                    </ContextLabel>
                    <ContextValue>{context.customer.email_masked}</ContextValue>
                  </ContextRow>
                )}
              </Card>
            )}

            {dueCount > 0 && dueTotalLabel && (
              <DueBanner role="note">
                <span aria-hidden="true">ℹ️</span>
                <DueBannerText>
                  {dueCount === 1
                    ? t("addPaymentSource.dueSummary.one", {
                        defaultValue:
                          "1 outstanding invoice totalling {{amount}} will be charged to this card after it is saved.",
                        variables: { amount: dueTotalLabel },
                      })
                    : t("addPaymentSource.dueSummary.many", {
                        defaultValue:
                          "{{count}} outstanding invoices totalling {{amount}} will be charged to this card after it is saved.",
                        variables: { count: dueCount, amount: dueTotalLabel },
                      })}
                </DueBannerText>
              </DueBanner>
            )}

            <Card>
              <PaymentMethodSelector
                selectedMethod={selectedMethod}
                onChange={setSelectedMethod}
                routingEntries={routingEntries}
                formData={formData}
                itemDetails={itemDetails}
                billingPlatformId={context?.billing_platform_id}
                planId={null}
                platformItemDetails={[]}
                selectedAddons={[]}
                checkoutPageConfDto={confDto}
                setCheckoutPageConfDto={setConfDto}
                isLoading={false}
                isRoutingLoaded={true}
                sessionId={sessionId}
                compact
                hideTitle={false}
              />

              <SubmitButton
                type="button"
                $color={confDto?.colour}
                disabled={!selectedMethod || submitting || sessionExpired}
                onClick={handleSubmit}
              >
                {dueCount > 0 && dueTotalLabel
                  ? t("addPaymentSource.submitAndPay", {
                      defaultValue: "Add payment method and pay {{amount}}",
                      variables: { amount: dueTotalLabel },
                    })
                  : t("addPaymentSource.submit", {
                      defaultValue: "Add payment method",
                    })}
              </SubmitButton>

              <SubmitNote>
                {dueCount > 0
                  ? t("addPaymentSource.submitNoteDue", {
                      defaultValue:
                        "Your card is verified with a zero-amount authorisation, then the outstanding balance is charged.",
                    })
                  : t("addPaymentSource.submitNote", {
                      defaultValue:
                        "Your card is verified with a zero-amount authorisation and saved for future payments.",
                    })}
              </SubmitNote>
            </Card>
          </>
        ) : (
          renderStatus()
        )}
      </Container>
    </Page>
  );
};

export default AddPaymentSourcePage;
